/** Track 2: speaker button + mixer popover (master / music / effects, mute, graphics quality). Settings persist in localStorage. */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { audio } from '../audio/index.ts';
import { getQualityPref, setQualityPref, type QualityPref } from '../graphics/quality.ts';

const useAudioSettings = () => useSyncExternalStore(audio.subscribe, audio.getSettings, audio.getSettings);

export function AudioControl() {
  const s = useAudioSettings();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [quality, setQuality] = useState<QualityPref>(() => getQualityPref());

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const slider = (label: string, key: 'master' | 'music' | 'sfx') => (
    <label className="flex items-center gap-2 text-xs text-slate-300">
      <span className="w-16">{label}</span>
      <input type="range" min={0} max={100} value={Math.round(s[key] * 100)} aria-label={label}
        onChange={e => audio.setSettings({ [key]: Number(e.target.value) / 100 })}
        className="flex-1" style={{ accentColor: '#60a5fa' }} />
    </label>
  );

  return (
    <div className="relative" ref={box}>
      <button type="button" className="nav-btn min-h-[44px] min-w-[44px] justify-center" aria-label="Hang beállításai" aria-expanded={open}
        data-testid="audio-button" title="Hang" onClick={() => setOpen(o => !o)}>
        <span aria-hidden className="text-base leading-none">{s.muted || s.master === 0 ? '🔇' : '🔊'}</span>
      </button>
      {open && (
        <div role="dialog" aria-label="Hang" data-testid="audio-panel"
          className="sci-fi-panel absolute right-0 top-full mt-1 z-50 w-56 max-w-[calc(100vw-1rem)] p-3 flex flex-col gap-2 rounded"
          style={{ border: '1px solid rgba(100,116,139,0.4)', background: 'rgba(15,23,42,0.97)' }}>
          {slider('Fő', 'master')}
          {slider('Zene', 'music')}
          {slider('Effektek', 'sfx')}
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={s.muted} onChange={e => audio.setSettings({ muted: e.target.checked })} data-testid="audio-mute" />
            Némítás
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300 pt-1" style={{ borderTop: '1px solid rgba(100,116,139,0.25)' }}>
            <span className="w-16">Grafika</span>
            <select value={quality} data-testid="quality-select" aria-label="Grafikai minőség"
              onChange={e => { const v = e.target.value as QualityPref; setQuality(v); setQualityPref(v); }}
              className="flex-1 bg-slate-900 rounded px-1 py-1" style={{ border: '1px solid rgba(100,116,139,0.3)' }}>
              <option value="auto">Automatikus</option>
              <option value="low">Alacsony</option>
              <option value="medium">Közepes</option>
              <option value="high">Magas</option>
            </select>
          </label>
          <p className="text-[10px] text-slate-500">A grafika az új nézeteknél lép életbe. Lassú eszközön a játék magától is visszaveszi.</p>
        </div>
      )}
    </div>
  );
}
